/**
 * @fileoverview Tool Registry System for Dynamic Tool Management
 * 
 * This file implements a comprehensive tool registry that manages the lifecycle
 * of interactive tools in the diagramming system. It provides registration,
 * activation, execution, and context management for all tools.
 * 
 * Key Features:
 * - Dynamic tool registration and unregistration
 * - Dependency management between tools
 * - Single and multi-tool activation modes
 * - Context-aware tool filtering and execution
 * - Event-driven tool lifecycle management
 * - Command integration for undoable operations
 * - UI location-based tool organization
 * 
 * Architecture:
 * - ToolRegistry: Central manager for all tools
 * - IToolRegistryOptions: Configuration for registry behavior
 * - IToolFilter: Criteria for filtering available tools
 * - Event-driven communication with tools and UI
 * - Context management for tool execution
 * 
 * Use Cases:
 * - Managing shape creation tools
 * - Handling editing and transformation tools
 * - Organizing tools by categories and UI locations
 * - Contextual tool availability based on selection
 * - Plugin-style tool extensibility
 * 
 * @author Claude Code
 * @version 1.0.0
 */

import { ITool, IToolContext, DiagramType, SelectionSummary, PresentationOwnership } from './interfaces/ITool';
import { IEventBus } from '../events/IEventBus';
import { CommandManager } from '../commands/CommandManager';
import { IFrame } from '../frames/IFrame';
import { KeyboardShortcutManager, ShortcutPriority } from '../keyboard/KeyboardShortcutManager';

/** UI locations a manifest can mount at (keys of IToolManifest.ui). */
export type ToolUiLocation =
  | 'toolbar'
  | 'contextMenu'
  | 'sidebar'
  | 'propertyPanel'
  | 'floatingPanel'
  | 'statusBar'
  | 'selectionAction'
  | 'canvasCreate'
  | 'canvasMode';

/** Chrome mounts: registry-ordered, ownership- and entitlement-gated at display time. */
const CHROME_MOUNT_LOCATIONS: ReadonlySet<ToolUiLocation> = new Set(['selectionAction', 'canvasCreate', 'canvasMode']);

/**
 * Configuration options for tool registry behavior.
 * 
 * Controls how the registry manages tool activation and lifecycle.
 * These options affect the overall user experience and tool interaction patterns.
 * 
 * @example
 * ```typescript
 * const options: IToolRegistryOptions = {
 *   autoActivateDefault: true,   // Auto-activate first registered tool
 *   allowMultipleActive: false   // Only one tool active at a time
 * };
 * ```
 */
export interface IToolRegistryOptions {
  /** Whether to automatically activate the first registered tool */
  autoActivateDefault?: boolean;
  
  /** Whether multiple tools can be active simultaneously */
  allowMultipleActive?: boolean;
}

/**
 * Filter criteria for querying available tools.
 * 
 * Provides flexible filtering based on tool properties, capabilities,
 * and UI requirements. Used for building dynamic tool palettes and
 * context-sensitive tool availability.
 * 
 * @example
 * ```typescript
 * // Get all shape creation tools for genogram diagrams
 * const creationTools = registry.getAvailableTools({
 *   category: 'creation',
 *   supportedDiagramTypes: ['genogram'],
 *   uiLocation: 'toolbar'
 * });
 * 
 * // Get tools that work with current selection
 * const editingTools = registry.getAvailableTools({
 *   requiresSelection: true,
 *   uiLocation: 'contextMenu'
 * });
 * ```
 */
export interface IToolFilter {
  /** Tool category (creation, editing, analysis, etc.) */
  category?: string;
  
  /** Diagram types the tool must support */
  supportedDiagramTypes?: DiagramType[];
  
  /** Whether the tool requires selected elements */
  requiresSelection?: boolean;
  
  /** UI location where the tool should appear */
  uiLocation?: 'toolbar' | 'contextMenu' | 'sidebar' | 'propertyPanel';
}

/**
 * Central registry for managing interactive tools in the diagramming system.
 * 
 * Provides comprehensive tool lifecycle management including registration,
 * activation, execution, and context management. Supports both single-tool
 * and multi-tool activation modes with dependency resolution and event coordination.
 * 
 * Key Responsibilities:
 * - Tool registration and dependency management
 * - Tool activation and deactivation with context awareness
 * - Command integration for undoable tool operations
 * - Event-driven communication between tools and UI
 * - Context updates based on selection and frame changes
 * - Tool filtering and organization by categories/UI locations
 * 
 * @example
 * ```typescript
 * const registry = new ToolRegistry(
 *   context,
 *   eventBus,
 *   commandManager,
 *   { autoActivateDefault: true, allowMultipleActive: false }
 * );
 * 
 * // Register tools
 * await registry.register(selectionTool);
 * await registry.register(shapeCreationTool);
 * 
 * // Activate a tool
 * registry.activateTool('selection-tool');
 * 
 * // Execute a tool
 * await registry.executeTool('create-person', { x: 100, y: 200 });
 * 
 * // Get tools for specific UI location
 * const toolbarTools = registry.getToolsForLocation('toolbar');
 * ```
 */
export class ToolRegistry {
  /** Map of registered tools by their ID */
  private tools: Map<string, ITool> = new Map();
  
  /** Currently active tool (null if none or multi-tool mode) */
  private activeTool: ITool | null = null;
  
  /** Set of active tools for multi-tool mode */
  private activeTools: Set<ITool> = new Set();
  
  /** Current execution context for tools */
  private context: IToolContext;
  
  /** Event bus for tool lifecycle communication */
  private eventBus: IEventBus;
  
  /** Command manager for undoable tool operations */
  private commandManager: CommandManager;
  
  /** Keyboard shortcut manager for centralized shortcut handling */
  private keyboardManager: KeyboardShortcutManager;
  
  /** Registry configuration options */
  private options: Required<IToolRegistryOptions>;
  
  /** Tool organization by categories */
  private toolGroups: Map<string, Set<string>> = new Map();
  
  /**
   * Creates a new tool registry instance.
   * 
   * @param context - Initial tool execution context
   * @param eventBus - Event bus for tool communication
   * @param commandManager - Command manager for undoable operations
   * @param options - Registry configuration options
   */
  constructor(
    context: IToolContext,
    eventBus: IEventBus,
    commandManager: CommandManager,
    options?: IToolRegistryOptions
  ) {
    this.context = context;
    this.eventBus = eventBus;
    this.commandManager = commandManager;
    this.keyboardManager = new KeyboardShortcutManager(eventBus);
    this.options = {
      autoActivateDefault: options?.autoActivateDefault ?? true,
      allowMultipleActive: options?.allowMultipleActive ?? false
    };
    
    this.setupEventListeners();
  }
  
  private setupEventListeners(): void {
    // Listen for context changes
    this.eventBus.on('selection:changed', (selection: Set<string>) => {
      this.updateContext({ selection });
    });
    
    this.eventBus.on('frame:activated', ({ current }: { current: IFrame | null }) => {
      this.updateContext({ frame: current });
    });
  }
  
  /**
   * Register a new tool with the registry.
   * 
   * Validates dependencies, initializes the tool, and adds it to appropriate
   * categories. Automatically activates the first tool if configured to do so.
   * 
   * @param tool - Tool instance to register
   * @throws Error if tool ID conflicts or dependencies are missing
   * 
   * @example
   * ```typescript
   * const selectionTool = new SelectionTool();
   * await registry.register(selectionTool);
   * ```
   */
  async register(tool: ITool): Promise<void> {
    if (this.tools.has(tool.manifest.id)) {
      // Idempotent no-op: re-registration (e.g. a tools plugin re-applied on remount)
      // is expected and safely skipped — not a duplicate-tool error. Kept at debug
      // level to avoid false "already registered" warnings during discovery.
      console.debug(`Tool "${tool.manifest.id}" already registered; skipping duplicate registration.`);
      return;
    }
    
    // Check dependencies
    if (tool.manifest.dependencies) {
      for (const depId of tool.manifest.dependencies) {
        if (!this.tools.has(depId)) {
          throw new Error(`Tool "${tool.manifest.id}" depends on "${depId}" which is not registered`);
        }
      }
    }
    
    // Initialize tool if needed
    if (tool.initialize) {
      await tool.initialize();
    }
    
    this.tools.set(tool.manifest.id, tool);
    
    // Add to category group
    const category = tool.manifest.category;
    if (!this.toolGroups.has(category)) {
      this.toolGroups.set(category, new Set());
    }
    this.toolGroups.get(category)!.add(tool.manifest.id);
    
    // Register keyboard shortcuts
    this.registerToolShortcuts(tool);
    
    this.eventBus.emit('tool:registered', tool);
    
    // Auto-activate if it's the first tool and option is enabled
    if (this.options.autoActivateDefault && this.tools.size === 1) {
      this.activateTool(tool.manifest.id);
    }
  }
  
  /**
   * Register multiple tools with dependency resolution.
   * 
   * Automatically sorts tools by dependencies using topological sort
   * to ensure dependencies are registered before dependent tools.
   * 
   * @param tools - Array of tools to register
   * @throws Error if circular dependencies are detected
   * 
   * @example
   * ```typescript
   * await registry.registerMany([
   *   selectionTool,
   *   transformTool,  // depends on selection
   *   creationTool
   * ]);
   * ```
   */
  async registerMany(tools: ITool[]): Promise<void> {
    // Sort by dependencies
    const sorted = this.topologicalSort(tools);
    
    for (const tool of sorted) {
      await this.register(tool);
    }
  }
  
  unregister(toolId: string): void {
    const tool = this.tools.get(toolId);
    if (!tool) return;
    
    // Check if other tools depend on this one
    const dependents = this.getToolsDependingOn(toolId);
    if (dependents.length > 0) {
      throw new Error(
        `Cannot unregister tool "${toolId}" because these tools depend on it: ${dependents.join(', ')}`
      );
    }
    
    // Deactivate if active
    if (this.activeTool?.manifest.id === toolId) {
      this.deactivateTool();
    }
    
    // Remove from active tools if multi-tool mode
    if (this.activeTools.has(tool)) {
      this.activeTools.delete(tool);
      tool.onDeactivate();
    }

    // A registry can outlive the canvas that registered its tools. Remove the
    // shortcut handlers together with the tool so a later canvas mount cannot
    // retain closures over the previous tool instance and its stale data.
    this.unregisterToolShortcuts(tool);
    
    // Dispose tool
    if (tool.dispose) {
      tool.dispose();
    }
    
    // Remove from registry
    this.tools.delete(toolId);
    
    // Remove from category group
    const category = tool.manifest.category;
    this.toolGroups.get(category)?.delete(toolId);
    
    this.eventBus.emit('tool:unregistered', tool);
  }
  
  // Tool retrieval
  getTool(toolId: string): ITool | undefined {
    return this.tools.get(toolId);
  }
  
  getAllTools(): ITool[] {
    return Array.from(this.tools.values());
  }
  
  getToolsByCategory(category: string): ITool[] {
    const toolIds = this.toolGroups.get(category);
    if (!toolIds) return [];
    
    return Array.from(toolIds)
      .map(id => this.tools.get(id))
      .filter(Boolean) as ITool[];
  }
  
  /**
   * Get tools that match the specified filter criteria.
   * 
   * Provides flexible tool querying based on category, diagram type support,
   * selection requirements, and UI location. Used for building dynamic
   * tool palettes and context-sensitive menus.
   * 
   * @param filter - Optional filter criteria
   * @returns Array of tools matching the filter
   * 
   * @example
   * ```typescript
   * // Get all creation tools for genogram diagrams
   * const creationTools = registry.getAvailableTools({
   *   category: 'creation',
   *   supportedDiagramTypes: ['genogram']
   * });
   * 
   * // Get tools that require selection
   * const editingTools = registry.getAvailableTools({
   *   requiresSelection: true
   * });
   * ```
   */
  getAvailableTools(filter?: IToolFilter): ITool[] {
    return Array.from(this.tools.values()).filter(tool => {
      // Apply filter if provided
      if (filter) {
        if (filter.category && tool.manifest.category !== filter.category) {
          return false;
        }
        
        if (filter.supportedDiagramTypes) {
          if (!tool.manifest.supportedDiagramTypes ||
              !filter.supportedDiagramTypes.some(type => 
                tool.manifest.supportedDiagramTypes!.includes(type))) {
            return false;
          }
        }
        
        if (filter.requiresSelection !== undefined) {
          if (tool.manifest.requiresSelection !== filter.requiresSelection) {
            return false;
          }
        }
        
        if (filter.uiLocation) {
          if (!tool.manifest.ui[filter.uiLocation]) {
            return false;
          }
        }
      }
      
      // Check if tool can execute in current context
      // Don't filter out tools that can't execute - let UI handle that
      return true;
    });
  }
  
  getToolsForLocation(location: ToolUiLocation, summary?: SelectionSummary): ITool[] {
    const isChromeMount = CHROME_MOUNT_LOCATIONS.has(location);
    const base = this.getAvailableTools().filter(tool => {
      if (tool.manifest.ui[location] !== true) return false;
      // Presentation-ownership gate applies at every location (Phase 2 uses
      // it to trim style tools when lenses own presentation). Exclusion is
      // silent — never an error.
      const modes = tool.manifest.applicability?.presentationModes;
      if (modes && !modes.includes(this.presentationOwnership())) {
        return false;
      }
      // Entitlement display-gate is chrome-mount-only: legacy surfaces keep
      // showing capability-gated tools and surface the upgrade modal on
      // click; chrome mounts hide them (fail closed while unresolved).
      if (isChromeMount && !this.hasRequiredCapabilities(tool)) {
        return false;
      }
      return true;
    });

    if (!summary) {
      // Filter by supportedDiagramTypes even when no summary
      return this.sortForLocation(location, base.filter(tool => {
        if (tool.manifest.supportedDiagramTypes && tool.manifest.supportedDiagramTypes.length > 0) {
          return tool.manifest.supportedDiagramTypes.includes(this.context.diagramType);
        }
        return true;
      }));
    }

    // Filter by applicability first
    const candidates = base.filter(tool => {
      const a = tool.manifest.applicability;
      if (a) {
        if (a.diagramTypes && !a.diagramTypes.includes(this.context.diagramType)) {
          return false;
        }
        if (a.contextTypes && !a.contextTypes.includes(summary.contextType)) {
          // In mixed mode, defer to intersection later by allowing temporarily
          if (summary.contextType !== 'mixed') return false;
        }
        if (a.requires) {
          for (const key of Object.keys(a.requires) as Array<keyof SelectionSummary['flags']>) {
            if (a.requires[key] === true && summary.flags[key] !== true) {
              return false;
            }
          }
        }
      } else {
        // If no applicability, check supportedDiagramTypes
        if (tool.manifest.supportedDiagramTypes && tool.manifest.supportedDiagramTypes.length > 0) {
          if (!tool.manifest.supportedDiagramTypes.includes(this.context.diagramType)) {
            return false;
          }
        }
      }
      return true;
    });

    if (summary.contextType !== 'mixed') return this.sortForLocation(location, candidates);

    // Intersection for mixed: keep tools that are applicable to all selected types
    const typeArray = Array.from(summary.typesSet).filter(t => t !== 'mixed' && t !== 'none');
    const intersected = candidates.filter(tool => {
      const a = tool.manifest.applicability;
      if (!a?.contextTypes || a.contextTypes.length === 0) return true; // treat as generic tool
      return typeArray.every(t => a.contextTypes!.includes(t));
    });
    // fallback to candidates (core set could be curated later)
    return this.sortForLocation(location, intersected.length > 0 ? intersected : candidates);
  }

  /**
   * Explain why each registered tool is or is not visible at a chrome mount.
   * Dev-only consumer: ChromeInspector. Runs the same gates as
   * getToolsForLocation but reports the first excluding gate instead of
   * filtering silently.
   */
  explainToolsForLocation(location: ToolUiLocation, summary?: SelectionSummary): Array<{ tool: ITool; visible: boolean; excludedBy?: string }> {
    const visibleIds = new Set(this.getToolsForLocation(location, summary).map(t => t.manifest.id));
    return Array.from(this.tools.values()).map(tool => {
      if (visibleIds.has(tool.manifest.id)) return { tool, visible: true };
      const m = tool.manifest;
      const a = m.applicability;
      let excludedBy = 'not registered for this mount';
      if (m.ui[location] === true) {
        if (a?.presentationModes && !a.presentationModes.includes(this.presentationOwnership())) {
          excludedBy = `applicability.presentationModes=[${a.presentationModes.join(',')}] (got '${this.presentationOwnership()}')`;
        } else if (!this.hasRequiredCapabilities(tool)) {
          excludedBy = `requiredCapabilities=[${(m.requiredCapabilities ?? []).join(',')}] (${this.context.entitlements ? 'not granted' : 'entitlements unresolved'})`;
        } else if (a?.diagramTypes && !a.diagramTypes.includes(this.context.diagramType)) {
          excludedBy = `applicability.diagramTypes=[${a.diagramTypes.join(',')}] (got '${this.context.diagramType}')`;
        } else if (summary && a?.contextTypes && !a.contextTypes.includes(summary.contextType)) {
          excludedBy = `applicability.contextTypes=[${a.contextTypes.join(',')}] (got '${summary.contextType}')`;
        } else if (!a && m.supportedDiagramTypes && m.supportedDiagramTypes.length > 0 && !m.supportedDiagramTypes.includes(this.context.diagramType)) {
          excludedBy = `supportedDiagramTypes=[${m.supportedDiagramTypes.join(',')}] (got '${this.context.diagramType}')`;
        }
      }
      return { tool, visible: false, excludedBy };
    });
  }

  private presentationOwnership(): PresentationOwnership {
    return this.context.presentationOwnership ?? 'user';
  }

  /**
   * Display-time mirror of the fail-closed capability check in executeTool:
   * unresolved entitlements hide the tool rather than allowing it.
   */
  private hasRequiredCapabilities(tool: ITool): boolean {
    if (!tool.manifest.requiredCapabilities) return true;
    const granted = this.context.entitlements?.capabilities.capabilities ?? [];
    return tool.manifest.requiredCapabilities.every((capability) => granted.includes(capability));
  }

  /**
   * Chrome mounts order registry-side (chrome.order asc, id tiebreak) so
   * hardcoded caller-side order consts can die. Legacy locations keep
   * registration order — their callers still own ordering.
   */
  private sortForLocation(location: ToolUiLocation, tools: ITool[]): ITool[] {
    if (!CHROME_MOUNT_LOCATIONS.has(location)) return tools;
    return [...tools].sort((a, b) => {
      const orderA = a.manifest.chrome?.order ?? 100;
      const orderB = b.manifest.chrome?.order ?? 100;
      if (orderA !== orderB) return orderA - orderB;
      return a.manifest.id.localeCompare(b.manifest.id);
    });
  }
  
  /**
   * Activate a tool for user interaction.
   * 
   * Deactivates the current tool (in single-tool mode) and activates
   * the specified tool if it can execute in the current context.
   * 
   * @param toolId - ID of the tool to activate
   * @returns true if tool was activated, false if tool not found or cannot execute
   * 
   * @example
   * ```typescript
   * // Activate selection tool with fallback
   * if (!registry.activateTool('selection-tool')) {
   *   // Fallback logic
   * }
   * ```
   */
  activateTool(toolId: string): boolean {
    const tool = this.tools.get(toolId);
    if (!tool) {
      console.warn(`Tool "${toolId}" not found. Available tools: ${Array.from(this.tools.keys()).join(', ')}`);
      return false;
    }
    
    if (!tool.canExecute(this.context)) {
      console.warn(`Tool "${toolId}" cannot be activated in current context. It may require selection or other conditions.`);
      return false;
    }
    
    if (this.options.allowMultipleActive) {
      // Multi-tool mode
      if (!this.activeTools.has(tool)) {
        tool.onActivate(this.context);
        this.activeTools.add(tool);
        this.activateToolShortcuts(tool);
        this.eventBus.emit('tool:activated', tool);
      }
    } else {
      // Single-tool mode
      if (this.activeTool?.manifest.id === toolId) {
        return true; // Already active
      }
      
      // Deactivate current tool
      if (this.activeTool) {
        this.activeTool.onDeactivate();
        this.deactivateToolShortcuts(this.activeTool);
        this.eventBus.emit('tool:deactivated', this.activeTool);
      }
      
      // Activate new tool
      tool.onActivate(this.context);
      this.activeTool = tool;
      this.activateToolShortcuts(tool);
      this.eventBus.emit('tool:activated', tool);
    }
    return true;
  }
  
  deactivateTool(toolId?: string): void {
    if (this.options.allowMultipleActive && toolId) {
      // Deactivate specific tool in multi-tool mode
      const tool = this.tools.get(toolId);
      if (tool && this.activeTools.has(tool)) {
        tool.onDeactivate();
        this.activeTools.delete(tool);
        this.deactivateToolShortcuts(tool);
        this.eventBus.emit('tool:deactivated', tool);
      }
    } else if (!this.options.allowMultipleActive) {
      // Deactivate single active tool
      if (this.activeTool) {
        this.activeTool.onDeactivate();
        this.deactivateToolShortcuts(this.activeTool);
        this.eventBus.emit('tool:deactivated', this.activeTool);
        this.activeTool = null;
      }
    } else {
      // Deactivate all tools in multi-tool mode
      this.activeTools.forEach(tool => {
        tool.onDeactivate();
        this.deactivateToolShortcuts(tool);
        this.eventBus.emit('tool:deactivated', tool);
      });
      this.activeTools.clear();
    }
  }
  
  getActiveTool(): ITool | null {
    return this.activeTool;
  }
  
  getActiveTools(): ITool[] {
    if (this.options.allowMultipleActive) {
      return Array.from(this.activeTools);
    }
    return this.activeTool ? [this.activeTool] : [];
  }
  
  isToolActive(toolId: string): boolean {
    const tool = this.tools.get(toolId);
    if (!tool) return false;
    
    if (this.options.allowMultipleActive) {
      return this.activeTools.has(tool);
    }
    return this.activeTool?.manifest.id === toolId;
  }
  
  /**
   * Execute a tool with the given parameters.
   * 
   * Runs the tool's execute method and integrates the result with the
   * command manager for undo/redo support. Handles both single commands
   * and batch operations.
   * 
   * @param toolId - ID of the tool to execute
   * @param params - Parameters to pass to the tool
   * @returns true if tool was executed, false if tool not found or cannot execute
   * 
   * @example
   * ```typescript
   * // Create a person at specific coordinates
   * const success = await registry.executeTool('create-person', {
   *   x: 100,
   *   y: 200,
   *   name: 'John Doe'
   * });
   * if (!success) {
   *   // Fallback logic
   * }
   * ```
   */
  async executeTool(toolId: string, params?: any): Promise<boolean> {
    const tool = this.tools.get(toolId);
    if (!tool) {
      console.warn(`Tool "${toolId}" not found. Available tools: ${Array.from(this.tools.keys()).join(', ')}`);
      return false;
    }
    
    // Check write permissions before executing tools
    const capabilities = tool.manifest.capabilities;
    const mutatesDiagram = capabilities?.createsShapes || capabilities?.modifiesShapes ||
      capabilities?.deletesShapes || capabilities?.createsConnectors ||
      capabilities?.modifiesConnectors || capabilities?.deletesConnectors;
    if (!this.context.canWrite || (this.context.isSavedAtlasView && mutatesDiagram)) {
      return false;
    }
    
    // Set the context on the tool before execution
    tool.onActivate(this.context);
    
    // Check server-resolved capability keys before execution. Missing
    // entitlements are unresolved, not permission to proceed.
    if (tool.manifest.requiredCapabilities) {
      if (!this.hasRequiredCapabilities(tool)) {
        // Emit "plan:blocked" event for UI to show upgrade modal
        const event = new CustomEvent('plan:blocked', {
          detail: {
            toolId,
            reason: 'This tool requires capabilities not available in your current plan',
            feature: 'shapes.create',
            current: this.context.entitlements?.planType || 'free',
            required: tool.manifest.requiredCapabilities
          }
        });
        window.dispatchEvent(event);
        return false;
      }
    }
    
    if (!tool.canExecute(this.context)) {
      console.warn(`Tool "${toolId}" cannot execute in current context. Context:`, this.context);
      return false;
    }
    
    const result = await tool.execute(params);
    if (result) {
      if (Array.isArray(result)) {
        // Execute batch of commands
        await this.commandManager.executeBatch(result, tool.manifest.name);
      } else {
        // Execute single command
        await this.commandManager.execute(result);
      }
    }
    
    this.eventBus.emit('tool:executed', { tool, params, result });
    return true;
  }
  
  /**
   * Update the tool execution context.
   *
   * Notifies all active tools of context changes and deactivates tools
   * that can no longer execute in the new context. This is typically
   * called when selection changes or frames are activated.
   *
   * @param updates - Partial context updates to apply
   *
   * @example
   * ```typescript
   * // Update selection context
   * registry.updateContext({
   *   selection: new Set(['shape-1', 'shape-2'])
   * });
   *
   * // Change active frame
   * registry.updateContext({
   *   frame: newFrame
   * });
   * ```
   */
  updateContext(updates: Partial<IToolContext>): void {
    const nextContext = { ...this.context, ...updates };
    const changedKeys = Object.keys(updates) as Array<keyof IToolContext>;
    const hasChanges = changedKeys.some((key) => this.context[key] !== nextContext[key]);
    if (!hasChanges) {
      return;
    }

    this.context = nextContext;

    // Notify all active tools
    const activeTools = this.getActiveTools();
    activeTools.forEach(tool => {
      if (updates.selection !== undefined) {
        tool.onSelectionChange(updates.selection);
      }
      if (updates.frame !== undefined) {
        tool.onFrameChange(updates.frame);
      }
      if (updates.diagramType !== undefined && tool.onDiagramTypeChange) {
        tool.onDiagramTypeChange(updates.diagramType);
      }
    });

    // Check if active tools can still execute
    activeTools.forEach(tool => {
      if (!tool.canExecute(this.context)) {
        if (this.options.allowMultipleActive) {
          this.deactivateTool(tool.manifest.id);
        } else {
          this.deactivateTool();
        }
      }
    });

    // Update keyboard shortcuts for all tools based on canExecute state
    // This allows contextual tools (like Delete) to have active shortcuts
    // even when they're not the "active tool"
    this.tools.forEach(tool => {
      // Skip tools that are already active (their shortcuts are managed separately)
      const isActiveTool = this.options.allowMultipleActive
        ? this.activeTools.has(tool)
        : this.activeTool?.manifest.id === tool.manifest.id;

      if (!isActiveTool && tool.manifest.shortcuts) {
        if (tool.canExecute(this.context)) {
          // Tool can execute in current context - activate its shortcuts
          this.activateToolShortcuts(tool);
        } else {
          // Tool cannot execute - deactivate its shortcuts
          this.deactivateToolShortcuts(tool);
        }
      }
    });

    this.eventBus.emit('tool:contextChanged', this.context);
  }
  
  getContext(): IToolContext {
    return { ...this.context };
  }
  
  // Helper methods
  private topologicalSort(tools: ITool[]): ITool[] {
    const sorted: ITool[] = [];
    const visited = new Set<string>();
    const visiting = new Set<string>();
    
    const visit = (tool: ITool) => {
      if (visited.has(tool.manifest.id)) return;
      if (visiting.has(tool.manifest.id)) {
        throw new Error('Circular dependency detected in tools');
      }
      
      visiting.add(tool.manifest.id);
      
      if (tool.manifest.dependencies) {
        for (const depId of tool.manifest.dependencies) {
          const depTool = tools.find(t => t.manifest.id === depId);
          if (depTool) {
            visit(depTool);
          }
        }
      }
      
      visiting.delete(tool.manifest.id);
      visited.add(tool.manifest.id);
      sorted.push(tool);
    };
    
    tools.forEach(visit);
    return sorted;
  }
  
  private getToolsDependingOn(toolId: string): string[] {
    const dependents: string[] = [];
    
    this.tools.forEach(tool => {
      if (tool.manifest.dependencies?.includes(toolId)) {
        dependents.push(tool.manifest.id);
      }
    });
    
    return dependents;
  }
  
  /**
   * Clean up the registry and dispose all tools.
   * 
   * Deactivates all active tools, disposes all registered tools,
   * and clears all internal state. Should be called when shutting
   * down the application or switching to a different tool system.
   * 
   * @example
   * ```typescript
   * // Clean shutdown
   * registry.dispose();
   * ```
   */
  dispose(): void {
    // Deactivate all tools
    this.deactivateTool();
    
    // Dispose all tools
    this.tools.forEach(tool => {
      if (tool.dispose) {
        tool.dispose();
      }
    });
    
    // Dispose keyboard manager
    this.keyboardManager.dispose();
    
    this.tools.clear();
    this.toolGroups.clear();
    this.activeTools.clear();
    this.activeTool = null;
  }
  
  /**
   * Register keyboard shortcuts for a tool.
   */
  private registerToolShortcuts(tool: ITool): void {
    if (!tool.manifest.shortcuts) {
      return;
    }
    
    tool.manifest.shortcuts.forEach((shortcut, index) => {
      const shortcutId = `${tool.manifest.id}-shortcut-${index}`;
      
      // Create handler that delegates through the registry so tools receive context
      const handler = async () => {
        if (tool.canExecute(this.context)) {
          if (tool.manifest.capabilities?.requiresUserInput) {
            this.activateTool(tool.manifest.id);
            return;
          }
          // For movement tools, determine direction from key
          let params: any = undefined;
          if (tool.manifest.id === 'movement-tool' || tool.manifest.id === 'genogram-movement') {
            switch (shortcut.key) {
              case 'ArrowUp':
                params = { direction: 'up' };
                break;
              case 'ArrowDown':
                params = { direction: 'down' };
                break;
              case 'ArrowLeft':
                params = { direction: 'left' };
                break;
              case 'ArrowRight':
                params = { direction: 'right' };
                break;
            }
          }
          await this.executeTool(tool.manifest.id, params);
        }
      };
      
      this.keyboardManager.registerShortcut(
        shortcutId,
        shortcut,
        ShortcutPriority.TOOL,
        handler,
        tool.manifest.name,
        false // Initially inactive
      );
    });
  }
  
  /**
   * Activate keyboard shortcuts for a tool.
   */
  private activateToolShortcuts(tool: ITool): void {
    if (!tool.manifest.shortcuts) {
      return;
    }
    
    tool.manifest.shortcuts.forEach((_, index) => {
      const shortcutId = `${tool.manifest.id}-shortcut-${index}`;
      this.keyboardManager.activateShortcut(shortcutId);
    });
  }
  
  /**
   * Deactivate keyboard shortcuts for a tool.
   */
  private deactivateToolShortcuts(tool: ITool): void {
    if (!tool.manifest.shortcuts) {
      return;
    }
    
    tool.manifest.shortcuts.forEach((_, index) => {
      const shortcutId = `${tool.manifest.id}-shortcut-${index}`;
      this.keyboardManager.deactivateShortcut(shortcutId);
    });
  }

  /**
   * Unregister keyboard shortcuts owned by a tool.
   */
  private unregisterToolShortcuts(tool: ITool): void {
    if (!tool.manifest.shortcuts) {
      return;
    }

    tool.manifest.shortcuts.forEach((_, index) => {
      const shortcutId = `${tool.manifest.id}-shortcut-${index}`;
      this.keyboardManager.unregisterShortcut(shortcutId);
    });
  }
  
  /**
   * Get the keyboard shortcut manager instance.
   */
  getKeyboardManager(): KeyboardShortcutManager {
    return this.keyboardManager;
  }
}
